import { findRoleTrack } from '../data/roleKeywords';
import { ACTION_VERBS } from '../data/vocabulary';
import { cleanList, firstWord, hasMetric, normalise, toBullets } from '../text';
import type { AudienceMode, RuleContext } from '../types';

/**
 * Facts pulled out of the profile for the rewrite templates.
 *
 * The one invariant that matters in `src/core/rewrite/`: **we never invent a
 * fact.** Anything we cannot read off the profile is emitted as a bracketed
 * placeholder like `[metric]`, and the UI marks those visibly so a variant reads
 * as a scaffold to fill in rather than a finished sentence to paste. A tool that
 * quietly fabricates a number on someone's LinkedIn profile is worse than no
 * tool at all — it puts a claim they cannot defend in front of an interviewer.
 */

const ACTION_VERB_SET = new Set(ACTION_VERBS);

/** Marks a value the user has to supply themselves. */
export const PLACEHOLDER = {
  role: '[your role]',
  specialisation: '[your main skills]',
  audience: '[who you help]',
  outcome: '[the outcome you deliver]',
  metric: '[metric]',
  proof: '[your strongest result — include a number]',
  contact: '[your email or portfolio link]',
  target: '[the roles you want]',
} as const;

export interface ProfileFacts {
  /** Target role label if chosen, else the most recent job title. */
  role: string;
  /** `true` when `role` came from the profile rather than a placeholder. */
  hasRole: boolean;
  /** Top skills, at most three, joined for use in a sentence. */
  specialisation: string;
  hasSpecialisation: boolean;
  /** Most recent organisation, or `''`. */
  organisation: string;
  /** Individual top skills, at most five. */
  topSkills: string[];
  /** Role keywords the profile is missing, at most five. */
  missingKeywords: string[];
  /**
   * The user's own best existing bullet — quantified and, ideally, opening with
   * an action verb. Quoted verbatim so the rewrite builds on real evidence.
   * `null` when nothing on the profile qualifies.
   */
  strongestBullet: string | null;
  /** Closing invitation appropriate to the audience mode. */
  callToAction: string;
}

/** Joins a short list the way a sentence would: "a, b & c". */
function joinNaturally(values: readonly string[]): string {
  if (values.length <= 1) return values[0] ?? '';
  const head = values.slice(0, -1).join(', ');
  const last = values[values.length - 1] ?? '';
  return `${head} & ${last}`;
}

function callToActionFor(mode: AudienceMode, target: string): string {
  switch (mode) {
    case 'student':
      return `Currently looking for ${target} — reach out at ${PLACEHOLDER.contact}.`;
    case 'jobSeeker':
      return `Open to ${target}. The fastest way to reach me is ${PLACEHOLDER.contact}.`;
    case 'recruiter':
      return `Hiring for ${target} — if that sounds like you, message me or apply at ${PLACEHOLDER.contact}.`;
    default:
      return `Always happy to talk about this kind of work — ${PLACEHOLDER.contact}.`;
  }
}

export function extractFacts(ctx: RuleContext): ProfileFacts {
  const { profile, mode, keywords } = ctx;

  const track = findRoleTrack(profile.targetRole);
  const recent = profile.experience[0];
  const recentTitle = normalise(recent?.title ?? '');

  const role = track?.label ?? recentTitle;
  const skills = cleanList(profile.skills);
  const topSkills = skills.slice(0, 5);
  const specialisation = joinNaturally(topSkills.slice(0, 3));

  const bullets = profile.experience.flatMap((entry) => toBullets(entry.description));
  const quantified = bullets.filter((bullet) => hasMetric(bullet));
  const strongestBullet =
    quantified.find((bullet) => ACTION_VERB_SET.has(firstWord(bullet))) ?? quantified[0] ?? null;

  const target = track ? `${track.label} roles` : PLACEHOLDER.target;

  return {
    role: role || PLACEHOLDER.role,
    hasRole: Boolean(role),
    specialisation: specialisation || PLACEHOLDER.specialisation,
    hasSpecialisation: Boolean(specialisation),
    organisation: normalise(recent?.organisation ?? ''),
    topSkills,
    missingKeywords: keywords.missing.slice(0, 5),
    strongestBullet,
    callToAction: callToActionFor(mode, target),
  };
}
